import { Form, Spin } from "antd";
import { type FC, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router";

import type { ProductProfileProductEntity } from "src/app/features/ProductProfile/types/subTypes/ProductProfileProductEntity";
import type { AntCustomFormField } from "../../types/types";
import { OrgUnitsProductsProfileTable } from "./OrgUnitsProductsProfileTable";
import {
    useLazyLoadOrgUnitProductsQuery,
    useLazyLoadProductsByParentOrgUnitQuery,
    useLazyLoadProductsByProfileQuery,
} from "./OrgUnitsProductsServerApi";

export interface OrgUnitsProductsProfileProps extends AntCustomFormField<ProductProfileProductEntity[]> {
    showForceValue?: boolean;
    readOnly?: boolean;
}

export const OrgUnitsProductsProfile: FC<OrgUnitsProductsProfileProps> = ({
    value = [],
    onChange,
    showForceValue = false,
    readOnly = false,
}) => {
    const form = Form.useFormInstance();

    const orgUnitId = Form.useWatch<string | number | undefined>("id", form);
    const parentUnit = Form.useWatch<number | undefined>("parentUnit", form);
    const productProfileId = Form.useWatch<number | undefined>("productProfileId", form);
    const childProductProfileId = Form.useWatch<number | undefined>("childProductProfileId", form);

    const [execQuery, { isFetching }] = useLazyLoadOrgUnitProductsQuery();
    const [execProfileQuery, { isFetching: isProfileFetching }] = useLazyLoadProductsByProfileQuery();
    const [execProductProfileQuery, { isFetching: isParentFetching }] = useLazyLoadProductsByParentOrgUnitQuery();

    const { id: routeId } = useParams();
    const isCreateForm = !routeId;

    const isInitialLoadRef = useRef(true);
    const prevProfileIdRef = useRef<number | undefined>(childProductProfileId);

    // Keep latest onChange in a ref so effect callbacks don't go stale
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    const setProducts = useCallback((products: ProductProfileProductEntity[]) => {
        onChangeRef.current?.(products);
    }, []);

    const loadInitialProducts = useCallback(() => {
        execQuery(orgUnitId ? { orgUnitId: typeof orgUnitId === "string" ? Number(orgUnitId) : orgUnitId } : undefined)
            .unwrap()
            .then(setProducts)
            .catch(() => setProducts([]));
    }, [orgUnitId, execQuery, setProducts]);

    // Initial load + reload when childProductProfileId changes
    useEffect(() => {
        const isChildProfileChanged = !isInitialLoadRef.current && prevProfileIdRef.current !== childProductProfileId;
        prevProfileIdRef.current = childProductProfileId;

        if (isChildProfileChanged && childProductProfileId) {
            // User selected a different child profile — reload from that profile
            execProfileQuery({ productProfileId: childProductProfileId })
                .unwrap()
                .then(setProducts)
                .catch(() => setProducts([]));
        } else if (isInitialLoadRef.current && (isCreateForm || orgUnitId !== undefined)) {
            isInitialLoadRef.current = false;
            loadInitialProducts();
        }
    }, [childProductProfileId, orgUnitId, isCreateForm, execProfileQuery, setProducts, loadInitialProducts]);

    const handleProductsChange = useCallback(
        (products: ProductProfileProductEntity[]) => {
            onChange?.(products);
        },
        [onChange],
    );

    const handleBulkProfileValue = useCallback(
        (productIds: number[]) => {
            if (!orgUnitId) return;
            execProductProfileQuery({
                orgUnitId,
                isParent: false,
                productIds: productIds.length > 0 ? productIds : undefined,
            })
                .unwrap()
                .then((fetchedProducts) => {
                    const profileMap = new Map((fetchedProducts || []).map((p) => [p.productId, p]));
                    const next = value.map((row) => {
                        const fetched = profileMap.get(row.productId);
                        if (!fetched) return row;
                        return {
                            ...row,
                            inheritanceType: "PROFILE",
                            enabled: fetched.enabled ?? row.enabled,
                            mandatory: fetched.mandatory ?? row.mandatory,
                        };
                    });
                    setProducts(next);
                })
                .catch(() => setProducts(value.map((row) => ({ ...row, inheritanceType: "PROFILE" }))));
        },
        [execProductProfileQuery, orgUnitId, value, setProducts],
    );

    const handleBulkParentValue = useCallback(
        (productIds: number[]) => {
            const targetId = orgUnitId || parentUnit;
            if (!targetId) return;
            execProductProfileQuery({
                orgUnitId: targetId,
                isParent: true,
                productIds: productIds.length > 0 ? productIds : undefined,
            })
                .unwrap()
                .then((fetchedProducts) => {
                    const parentMap = new Map((fetchedProducts || []).map((p) => [p.productId, p]));
                    const next = value.map((row) => {
                        const fetched = parentMap.get(row.productId);
                        if (!fetched) return row;
                        return {
                            ...row,
                            inheritanceType: "PARENT",
                            enabled: fetched.enabled ?? row.enabled,
                            mandatory: fetched.mandatory ?? row.mandatory,
                        };
                    });
                    setProducts(next);
                })
                .catch(() => setProducts(value.map((row) => ({ ...row, inheritanceType: "PARENT" }))));
        },
        [orgUnitId, parentUnit, value, execProductProfileQuery, setProducts],
    );

    return (
        <Spin spinning={isFetching || isProfileFetching || isParentFetching}>
            <OrgUnitsProductsProfileTable
                products={value}
                onProductsChange={handleProductsChange}
                showForceValue={showForceValue}
                readOnly={readOnly}
                onBulkProfileValue={handleBulkProfileValue}
                onBulkParentValue={handleBulkParentValue}
                productProfileId={productProfileId}
                childProductProfileId={childProductProfileId}
            />
        </Spin>
    );
};
